import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import style from './style.module.css';
import classNames from 'classnames';
import moment from 'moment';
import PersonIcon from '@material-ui/icons/Person';
import {IDraggableBookingCard} from "app/floorplan-v2/DraggableBookingCard/types";
import {FloorplanV2State} from "floorplan-v2/app/appRoot/rootReducer";
import {DiaryRootState} from "appRoot/rootReducer";
import {useAppDispatch} from "appRoot/Store";
import useRequestAnimationFrame from "app/floorplan-v2/hooks/useRequestAnimationFrame";
import {DRAGGABLE_CARD_H, DRAGGABLE_CARD_W} from "floorplan-v2/app/pages/FloorPlan/types";
import LocationService from "external-services/location-service";
import FloorplanNonReduxState from "floorplan-v2/app/services/floorplanNonReduxState";


const NS = 'DraggableBookingCard';

const isIosOrAndroid = LocationService.isIOS() || LocationService.isAndroid();

const DraggableBookingCard = ({}: IDraggableBookingCard) => {

  const draggedBooking = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.draggedBooking);
  const draggedBookingPoint = useSelector((state: FloorplanV2State) => state.floorplanTableDragReducer.draggedBookingPoint);
  const bookingCardListWidth = useSelector((state: DiaryRootState) => state.resizerReducer.width);

  const outerRef = useRef<HTMLDivElement>();
  const cardRef = useRef<HTMLDivElement>();

  const fullName = useMemo(() => {
    return `${draggedBooking?.firstName || ''} ${draggedBooking?.lastName || ''}`;
  }, [draggedBooking?.firstName, draggedBooking?.lastName]);

  const {
    startTimeValue,
    startTimePeriod,
  }= useMemo(() => {
    const startTimeValue = moment(draggedBooking?.time)?.format('h:mm');
    const startTimePeriod = moment(draggedBooking?.time)?.format('A')
    return {
      startTimeValue, startTimePeriod,
    }
  }, [draggedBooking?.time]);


  const covers = draggedBooking?.people;

  useEffect(() => {
    if (draggedBooking) {
      setTimeout(() => {
        if (outerRef.current) {
          outerRef.current.style.transition = `transform 0.1s linear`;
          cardRef.current.style.transform = `rotate(5deg)`;
        }
      }, 100);
    }
  }, [draggedBooking]);

  useRequestAnimationFrame(() => {
    /**
     * Note: using window.nbi.floorplanV2.draggableBookingCoords instead of redux to store values for 2 reasons:
     * - faster read/write time
     * - window.requestAnimationFrame was more performant when updating css styles directly on the DOM
     */
    const newCoordsPoint = FloorplanNonReduxState.getDraggableBookingCoords();

    // console.log('++++newCoordsPoint', newCoordsPoint)

    if (!newCoordsPoint || !bookingCardListWidth) {
      return;
    }

    // yShift: moved slightly up so you can still read the card (slightly more for fat fingers on touch devices)
    const yShift = isIosOrAndroid ? 40 : 25;
    const newX = newCoordsPoint.x - (DRAGGABLE_CARD_W/2);
    const newY = newCoordsPoint.y - (DRAGGABLE_CARD_H/2) - yShift;
    outerRef.current.style.transform = `translate(${newX}px,${newY}px)`;
  }, {shouldAnimate: !!draggedBooking})

  if (!draggedBookingPoint || !draggedBooking) {
    return <></>;
  }

  return (
    <>
      {draggedBooking && (
        <div
          data-testid="root"
          ref={outerRef}
          className={style.root}
          style={{
            width: DRAGGABLE_CARD_W,
            height: DRAGGABLE_CARD_H
          }}
        >
          <div
            ref={cardRef}
            className={style.cardRoot}
          >
            <div
              className={classNames({
                [style.detailContainer]: true,
                [style.withDivider]: false,
              })}
            >
              <div className={style.detailContainerRow}>
                <div
                  className={classNames({
                    [style.userName]: true,
                    [style.detailContainerRowChild]: true,
                    [style.ellipsisElement]: true,
                  })}
                >
              <span
                className={classNames({
                  [style.ellipsisElement]: true,
                })}
              >
                {draggedBooking?.company
                  ? `${fullName}, ${draggedBooking?.company}`
                  : `${fullName}`}
              </span>
                </div>
              </div>
              <div className={style.detailContainerRow}>
                <div
                  className={classNames({
                    [style.bookTime]: true,
                  })}
                >
              <span>
               {`${startTimeValue}`}
                <span className={style.timeSmaller}>{`${startTimePeriod}`}</span>
              </span>
                </div>
                <span className={style.centerItems}>
              {covers < 100 && <PersonIcon className={style.iconStyle}> </PersonIcon>}
                  <span>{covers}</span>
            </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DraggableBookingCard;
